import { useChartContext } from '../core/chart-context';
import type { XTick } from '../core/scales';

export interface XAxisProps {
  /** Formats the tick label */
  format?: (value: string | number) => string;
  /** Renders the baseline under the ticks */
  axisLine?: boolean;
  /** Tick count hint for continuous scales */
  tickCount?: number;
  /** Rotates labels (degrees, e.g. -45) when they would otherwise overlap */
  labelAngle?: number;
}

/** Approximate label width in px — enough for collision decisions without layout. */
const estimateWidth = (label: string): number => label.length * 7;

/** Thins ticks so labels never overlap, always keeping first and last. */
export function thinTicks(ticks: XTick[], innerWidth: number, labelWidths: number[]): XTick[] {
  if (ticks.length <= 2) return ticks;
  const maxLabel = Math.max(...labelWidths, 1);
  const capacity = Math.max(Math.floor(innerWidth / (maxLabel + 8)), 2);
  if (ticks.length <= capacity) return ticks;

  const stride = Math.ceil(ticks.length / capacity);
  const thinned = ticks.filter((_, index) => index % stride === 0);
  const last = ticks[ticks.length - 1];
  if (thinned[thinned.length - 1] !== last) thinned.push(last);
  return thinned;
}

/** X axis rendered as text labels under the drawing area, with overflow thinning. */
export const XAxis = ({ format, axisLine = true, tickCount = 5, labelAngle }: XAxisProps) => {
  const { xScale, dimensions } = useChartContext();
  const allTicks = xScale.ticks(tickCount);
  const labels = allTicks.map(tick => (format ? format(tick.value) : tick.label));
  const ticks = thinTicks(allTicks, dimensions.innerWidth, labels.map(estimateWidth));

  return (
    <g data-slot='chart-x-axis' transform={`translate(0,${dimensions.innerHeight})`}>
      {axisLine && (
        <line x1={0} x2={dimensions.innerWidth} y1={0} y2={0} stroke='var(--color-border-base)' />
      )}
      {ticks.map(tick => {
        const label = format ? format(tick.value) : tick.label;
        return (
          <text
            key={`${tick.value}`}
            x={tick.position}
            y={16}
            textAnchor={labelAngle ? 'end' : 'middle'}
            fontSize={12}
            fill='var(--color-foreground-muted)'
            transform={labelAngle ? `rotate(${labelAngle},${tick.position},16)` : undefined}
          >
            {label}
          </text>
        );
      })}
    </g>
  );
};

XAxis.displayName = 'XAxis';
